import { useState } from 'react';
import { useLogin } from '@/hooks/use-auth';
import { PasswordInput } from '@/components/ui/password-input';
import { AuthLayout } from '@/components/layout/auth-layout';
import { TwoFactorVerify } from './two-factor-verify';
import { useBrandingStore } from '@/stores/branding-store';

export function LoginPage() {
  const login = useLogin();
  const { config: branding } = useBrandingStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending2FA, setPending2FA] = useState<{ pendingToken: string } | null>(null);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Please enter a valid email';
    if (!password) errors.password = 'Password is required';
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const result = await login.mutateAsync({ email, password });
      if (result.requires2FA) {
        setPending2FA({ pendingToken: result.pendingToken });
        return;
      }
      window.location.hash = '#/';
    } catch (err: any) {
      setError(err?.error?.message || 'Login failed');
    }
  };

  if (pending2FA) {
    return (
      <TwoFactorVerify
        pendingToken={pending2FA.pendingToken}
        isAdmin={false}
        onBack={() => {
          setPending2FA(null);
          setPassword('');
        }}
      />
    );
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm lg:max-w-[430px] space-y-6 rounded-xl border border-border bg-white/80 p-8 shadow-sm backdrop-blur-sm">
        <div className="text-center">
          <img
            src={branding.logoUrl || '/logo.png'}
            alt={branding.appName || 'Q PaymentZ'}
            className="mx-auto h-10 w-auto sm:h-12 lg:h-14 object-contain"
          />
          <p className="mt-3 text-[18px] text-muted-foreground">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

          <div>
            <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: '' }));
              }}
              autoFocus
              className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
              placeholder="you@example.com"
            />
            {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
          </div>

          <div>
            <label className="mb-1 block text-[17px] font-medium text-[#2c3345]">Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((prev) => ({ ...prev, password: '' }));
              }}
              className={`h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${fieldErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
              placeholder="Enter your password"
            />
            {fieldErrors.password && <p className="mt-1 text-xs text-red-500">{fieldErrors.password}</p>}
            <div className="mt-1 text-right">
              <a href="#/forgot-password" className="text-xs text-primary hover:underline">
                Forgot Password?
              </a>
            </div>
          </div>

          <button
            type="submit"
            disabled={login.isPending}
            className="h-10 w-full rounded-lg bg-primary text-[17px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {login.isPending ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{' '}
          <a href="#/signup" className="font-medium text-primary hover:underline">
            Sign Up
          </a>
        </p>
      </div>
    </AuthLayout>
  );
}
